import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import shortid from 'shortid';

import Modal from '../../lib/modal';
import DivButton from '../button/DivButton';
import { UploadImage } from '../../common/S3';
import { SERVER_URL } from '../../common/Settings';
import {
  checkedCurrencySymbol,
  returnComma,
  getBase64,
  getAddonPrice,
} from '../../common/Utils';

const Close = '/svg/close.svg';

Modal.defaultStyles.overlay.backgroundColor = 'rgba(0, 0, 0, 0.5)';

// 언어별 필드 반환. 해당 언어 미입력 옵션이 빈 이름으로 노출되지 않게 fallback
function getAddonTitle(addon, language) {
  switch (language) {
    case 'ko':
      return addon.title || '';
    case 'ja':
      return addon.title_jp || addon.title_eng || addon.title || '';
    default:
      return addon.title_eng || addon.title || '';
  }
}

// 참고 이미지 업로드 상한. 운영 nginx client_max_body_size 상향과 세트 (base64 전송으로 37% 팽창)
const MAX_REF_IMG_BYTES = 2 * 1024 * 1024;

const REF_IMG_HINT = {
  en: 'Maximum 2MB per image',
  ko: '이미지당 최대 2MB',
  ja: '画像1枚あたり最大2MB',
};

const REF_IMG_SIZE_ERROR = {
  en: 'Only image files under 2MB are allowed.\nPlease reduce the file size and try again, or send the image to us by email if the file is too large.',
  ko: '2MB 이하의 이미지만 업로드할 수 있습니다.\n용량을 줄여 다시 시도하시거나, 파일이 너무 크면 이메일로 보내주세요.',
  ja: '2MB以下の画像のみアップロードできます。\nサイズを小さくして再度お試しいただくか、大きい場合はメールでお送りください。',
};

const REF_IMG_UPLOAD_ERROR = {
  en: 'Image upload failed.\nPlease try again, or send the image to us by email.',
  ko: '이미지 업로드에 실패했습니다.\n다시 시도하시거나, 이메일로 보내주세요.',
  ja: '画像のアップロードに失敗しました。\n再度お試しいただくか、メールでお送りください。',
};

function getAddonSize(addon, language) {
  switch (language) {
    case 'ko':
      return addon.size || '';
    case 'ja':
      return addon.size_jp || '';
    default:
      return addon.size_eng || '';
  }
}

function getAddonMemo(addon, language) {
  switch (language) {
    case 'ko':
      return addon.memo || '';
    case 'ja':
      return addon.memo_jp || '';
    default:
      return addon.memo_eng || '';
  }
}

// 카테고리 매핑
const CATEGORY_MAP = {
  en: {
    personalization: 'Personalization',
    glove_model: 'Glove Model',
    lace_style: 'Lace Style',
    others: 'Others',
  },
  ko: {
    personalization: '개인화',
    glove_model: '글러브 모델',
    lace_style: '레이스 스타일',
    others: '기타',
  },
  ja: {
    personalization: 'パーソナライゼーション',
    glove_model: 'グラブモデル',
    lace_style: 'レーススタイル',
    others: 'その他',
  },
};

// 헤더 텍스트 매핑
const HEADER_TEXT = {
  en: {
    category: 'Category',
    image: 'Image',
    option: 'Option',
    price: 'Price',
    size: 'Size',
    memo: 'Memo',
  },
  ko: {
    category: '카테고리',
    image: '이미지',
    option: '옵션명',
    price: '가격',
    size: '사이즈',
    memo: '메모',
  },
  ja: {
    category: 'カテゴリー',
    image: '画像',
    option: 'オプション',
    price: '価格',
    size: 'サイズ',
    memo: 'メモ',
  },
};

function getCategoryText(category, language) {
  const lang = CATEGORY_MAP[language] || CATEGORY_MAP.en;
  const key = (category || '').toLowerCase().replace(/\s+/g, '_');
  return lang[key] || category || '-';
}

function getHeaderText(language) {
  return HEADER_TEXT[language] || HEADER_TEXT.en;
}

class AddOnOptionModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      addonProducts: [],
      selectedAddons: props.existingAddons ? [...props.existingAddons] : [],
      addonRequest: props.existingRequest || '',
      refImages: props.existingImages ? [...props.existingImages] : [],
      isLoading: true,
      uploadingImage: false,
    };
    this.fileInput = null;
  }

  componentDidMount() {
    this.fetchAddonProducts();
  }

  async fetchAddonProducts() {
    try {
      const response = await fetch(`${SERVER_URL}/gloveworks/restapi/addon_product/get`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      let data = await response.json();

      if (typeof data === 'string') {
        data = JSON.parse(data);
      }

      this.setState({
        addonProducts: data.results || data || [],
        isLoading: false,
      });
    } catch (e) {
      console.log('Failed to fetch addon products:', e);
      this.setState({ isLoading: false, addonProducts: [] });
    }
  }

  handleAddonToggle = (addon) => {
    const { selectedAddons } = this.state;
    const isSelected = selectedAddons.find(a => a.pid_addon === addon.pid_addon);

    if (isSelected) {
      this.setState({
        selectedAddons: selectedAddons.filter(a => a.pid_addon !== addon.pid_addon),
      });
    } else {
      this.setState({
        selectedAddons: [...selectedAddons, addon],
      });
    }
  };

  handleRequestChange = (e) => {
    this.setState({ addonRequest: e.target.value });
  };

  handleImageUpload = async (e) => {
    const { refImages } = this.state;
    const { i18n } = this.props;
    const lang = (i18n && i18n.language) || 'en';
    const files = Array.from(e.target.files);

    if (refImages.length + files.length > 4) {
      alert(this.props.t('addonMaxImages') || 'Maximum 4 images allowed');
      return;
    }

    // 용량 사전 검증. 초과 파일은 사유 안내 후 제외하고 나머지만 업로드
    const oversize = files.filter(file => file.size > MAX_REF_IMG_BYTES);
    const validFiles = files.filter(file => file.size <= MAX_REF_IMG_BYTES);
    if (oversize.length > 0) {
      alert(REF_IMG_SIZE_ERROR[lang] || REF_IMG_SIZE_ERROR.en);
    }
    if (validFiles.length === 0) {
      if (this.fileInput) this.fileInput.value = '';
      return;
    }

    this.setState({ uploadingImage: true });

    try {
      for (const file of validFiles) {
        if (refImages.length >= 4) break;

        const base64 = await getBase64(file);
        const uploadResult = await UploadImage(base64, 'customize/addon');

        this.setState(prevState => ({
          refImages: [...prevState.refImages, {
            url: uploadResult.Location,
            key: uploadResult.Key,
          }],
        }));
      }
    } catch (err) {
      console.log('Image upload failed:', err);
      alert(REF_IMG_UPLOAD_ERROR[lang] || REF_IMG_UPLOAD_ERROR.en);
    }

    this.setState({ uploadingImage: false });
    if (this.fileInput) this.fileInput.value = '';
  };

  handleRemoveImage = (index) => {
    this.setState(prevState => ({
      refImages: prevState.refImages.filter((_, i) => i !== index),
    }));
  };

  calculateTotalCost = () => {
    const { selectedAddons } = this.state;
    const { i18n } = this.props;

    return selectedAddons.reduce((total, addon) => {
      return total + getAddonPrice(addon, i18n.language);
    }, 0);
  };

  handleConfirm = () => {
    const { selectedAddons, addonRequest, refImages } = this.state;
    const { onConfirm } = this.props;

    const addonData = {
      addon_ids: selectedAddons.map(a => a.pid_addon),
      addons: selectedAddons,
      addon_request: addonRequest,
      addon_ref_imgs: refImages,
      addon_cost: this.calculateTotalCost(),
    };

    onConfirm(addonData);
  };

  renderTableRow = (addon) => {
    const { selectedAddons } = this.state;
    const { i18n } = this.props;
    const isSelected = selectedAddons.find(a => a.pid_addon === addon.pid_addon);
    const price = getAddonPrice(addon, i18n.language);

    return (
      <tr
        key={addon.pid_addon}
        style={styles.tableRow(isSelected)}
        onClick={() => this.handleAddonToggle(addon)}
      >
        <td style={styles.checkboxCell}>
          <div style={styles.checkbox(isSelected)}>
            {isSelected && <span style={styles.checkmark}>✓</span>}
          </div>
        </td>
        <td style={styles.categoryCell}>
          <span style={styles.productCategory}>{getCategoryText(addon.category, i18n.language)}</span>
        </td>
        <td style={styles.imageCell}>
          {addon.img_url ? (
            <img src={addon.img_url} alt={addon.title} style={styles.productImage} />
          ) : (
            <div style={styles.noImage}>No Image</div>
          )}
        </td>
        <td style={styles.titleCell}>
          <span style={styles.productTitle}>{getAddonTitle(addon, i18n.language)}</span>
        </td>
        <td style={styles.priceCell}>
          <span style={styles.productPrice}>
            {checkedCurrencySymbol(i18n.language)}
            {i18n.language === 'en' ? Number(price).toFixed(2) : returnComma(Number(price))}
          </span>
        </td>
        <td style={styles.sizeCell}>
          <span style={styles.productSize}>{getAddonSize(addon, i18n.language) || '-'}</span>
        </td>
        <td style={styles.memoCell}>
          <span style={styles.productMemo}>{getAddonMemo(addon, i18n.language) || '-'}</span>
        </td>
      </tr>
    );
  };

  render() {
    const { isModal, onClose, i18n, t } = this.props;
    const { addonProducts, addonRequest, refImages, isLoading, uploadingImage } = this.state;
    const totalCost = this.calculateTotalCost();

    return (
      <Modal
        ariaHideApp={false}
        isOpen={isModal}
        contentLabel="Add Options"
        onRequestClose={onClose}
        style={styles.modal}
        portalClassName="addon-modal-portal"
      >
        <DivButton style={styles.closeBtn} onClick={onClose}>
          <img src={Close} alt="close" />
        </DivButton>

        <div style={styles.header}>
          <h2 style={styles.title}>{t('addonTitle') || 'Add Options'}</h2>
        </div>

        <div className="addon-modal-content" style={styles.content}>
          {/* 테이블 영역 */}
          <div className="addon-table-container" style={styles.tableContainer}>
            <div className="addon-table-scroll-wrapper" style={styles.tableScrollWrapper}>
              <table className="addon-table" style={styles.table}>
                <thead>
                  <tr style={styles.tableHeaderRow}>
                    <th style={styles.thCheckbox}></th>
                    <th style={styles.thCategory}>{getHeaderText(i18n.language).category}</th>
                    <th style={styles.thImage}>{getHeaderText(i18n.language).image}</th>
                    <th style={styles.thTitle}>{getHeaderText(i18n.language).option}</th>
                    <th style={styles.thPrice}>{getHeaderText(i18n.language).price}</th>
                    <th style={styles.thSize}>{getHeaderText(i18n.language).size}</th>
                    <th style={styles.thMemo}>{getHeaderText(i18n.language).memo}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="7" style={styles.loadingCell}>Loading...</td>
                    </tr>
                  ) : addonProducts.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={styles.loadingCell}>No options available</td>
                    </tr>
                  ) : (
                    [...addonProducts].sort((a, b) => (a.category || '').localeCompare(b.category || '')).map(addon => this.renderTableRow(addon))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* 하단 섹션: 이미지 업로드 & 요청사항 */}
          <div className="addon-bottom-section" style={styles.bottomSection}>
            <div className="addon-bottom-left" style={styles.bottomLeft}>
              <p style={styles.sectionLabel}>
                {t('addonRefImages') || 'Reference Images'} ({refImages.length}/4)
              </p>
              <div style={styles.imageUploadArea}>
                {refImages.map((img, index) => (
                  <div key={shortid.generate()} style={styles.uploadedImageContainer}>
                    <img src={img.url} alt={`ref-${index}`} style={styles.uploadedImage} />
                    <button
                      style={styles.removeImageBtn}
                      onClick={() => this.handleRemoveImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {refImages.length < 4 && (
                  <label style={styles.uploadBtn}>
                    <input
                      ref={el => this.fileInput = el}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={this.handleImageUpload}
                      style={{ display: 'none' }}
                      disabled={uploadingImage}
                    />
                    {uploadingImage ? '...' : '+'}
                  </label>
                )}
              </div>
              <p style={{ color: '#e85454', fontSize: 12, marginTop: 6, marginBottom: 0 }}>
                {REF_IMG_HINT[i18n.language] || REF_IMG_HINT.en}
              </p>
            </div>
            <div className="addon-bottom-right" style={styles.bottomRight}>
              <p style={styles.sectionLabel}>{t('addonRequest') || 'Special Request'}</p>
              <textarea
                style={styles.textarea}
                value={addonRequest}
                onChange={this.handleRequestChange}
                placeholder={t('addonRequestPlaceholder') || 'Enter any special requests...'}
                maxLength={500}
              />
            </div>
          </div>
        </div>

        <div className="addon-footer" style={styles.footer}>
          <div style={styles.totalBox}>
            <span style={styles.totalLabel}>{t('addonTotal') || 'Total'}:</span>
            <span style={styles.totalPrice}>
              {checkedCurrencySymbol(i18n.language)}
              {i18n.language === 'en' ? Number(totalCost).toFixed(2) : returnComma(Number(totalCost))}
            </span>
          </div>
          <div style={styles.buttonBox}>
            <DivButton style={styles.cancelBtn} onClick={onClose}>
              {t('addonCancel') || 'Cancel'}
            </DivButton>
            <DivButton style={styles.confirmBtn} onClick={this.handleConfirm}>
              {t('addonConfirm') || 'Confirm'}
            </DivButton>
          </div>
        </div>
      </Modal>
    );
  }
}

AddOnOptionModal.propTypes = {
  isModal: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  i18n: PropTypes.object,
  t: PropTypes.func,
  existingAddons: PropTypes.array,
  existingRequest: PropTypes.string,
  existingImages: PropTypes.array,
};

AddOnOptionModal.defaultProps = {
  isModal: false,
  onClose: () => {},
  onConfirm: () => {},
  i18n: {},
  t: (key) => key,
  existingAddons: [],
  existingRequest: '',
  existingImages: [],
};

const styles = {
  modal: {
    content: {
      width: 1000,
      maxWidth: '95vw',
      height: '70vh',
      maxHeight: '70vh',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      padding: 0,
      borderRadius: 4,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    overlay: {
      zIndex: 1000,
    },
  },
  closeBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 10,
    cursor: 'pointer',
  },
  header: {
    padding: '10px 16px',
    borderBottom: '1px solid #ddd',
    backgroundColor: '#f8f8f8',
  },
  title: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#251f1b',
    margin: 0,
    fontFamily: 'Roboto Condensed, sans-serif',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  // 테이블 컨테이너
  tableContainer: {
    flex: 1,
    overflow: 'hidden',
    borderBottom: '1px solid #ddd',
  },
  tableScrollWrapper: {
    width: '100%',
    height: '100%',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    fontFamily: 'Roboto Condensed, sans-serif',
  },
  tableHeaderRow: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ddd',
  },
  thCheckbox: {
    width: 32,
    padding: '6px 4px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
    borderRight: '1px solid #eee',
  },
  thCategory: {
    width: 70,
    padding: '6px 4px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
    borderRight: '1px solid #eee',
  },
  thImage: {
    width: 70,
    padding: '6px 4px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
    borderRight: '1px solid #eee',
  },
  thTitle: {
    width: 130,
    padding: '6px 4px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
    borderRight: '1px solid #eee',
  },
  thPrice: {
    width: 65,
    padding: '6px 4px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
    borderRight: '1px solid #eee',
  },
  thSize: {
    width: 50,
    padding: '6px 4px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
    borderRight: '1px solid #eee',
  },
  thMemo: {
    padding: '6px 4px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
  },
  tableRow: (isSelected) => ({
    cursor: 'pointer',
    backgroundColor: isSelected ? 'rgba(199, 123, 70, 0.1)' : '#fff',
    borderBottom: '1px solid #eee',
    transition: 'background-color 0.15s ease',
  }),
  checkboxCell: {
    width: 32,
    padding: '4px',
    textAlign: 'center',
    verticalAlign: 'middle',
    borderRight: '1px solid #eee',
  },
  checkbox: (isSelected) => ({
    width: 18,
    height: 18,
    border: isSelected ? '2px solid #c77b46' : '2px solid #ccc',
    borderRadius: 2,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isSelected ? '#c77b46' : '#fff',
  }),
  checkmark: {
    color: '#fff',
    fontSize: 11,
    fontWeight: 'bold',
  },
  categoryCell: {
    width: 70,
    padding: '4px',
    textAlign: 'center',
    verticalAlign: 'middle',
    borderRight: '1px solid #eee',
  },
  productCategory: {
    fontSize: 10,
    color: '#555',
    fontFamily: 'Roboto Condensed, sans-serif',
  },
  imageCell: {
    width: 70,
    padding: '4px',
    textAlign: 'center',
    verticalAlign: 'middle',
    borderRight: '1px solid #eee',
  },
  productImage: {
    width: 56,
    height: 56,
    objectFit: 'contain',
    borderRadius: 2,
    backgroundColor: '#f9f9f9',
  },
  noImage: {
    width: 56,
    height: 56,
    backgroundColor: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 9,
    color: '#999',
    borderRadius: 2,
  },
  titleCell: {
    width: 130,
    padding: '4px',
    verticalAlign: 'middle',
    borderRight: '1px solid #eee',
  },
  productTitle: {
    fontSize: 11,
    fontWeight: '600',
    color: '#251f1b',
    fontFamily: 'Roboto Condensed, sans-serif',
  },
  priceCell: {
    width: 65,
    padding: '4px',
    textAlign: 'right',
    verticalAlign: 'middle',
    borderRight: '1px solid #eee',
  },
  productPrice: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#c77b46',
    fontFamily: 'Roboto Condensed, sans-serif',
  },
  sizeCell: {
    width: 50,
    padding: '4px',
    textAlign: 'center',
    verticalAlign: 'middle',
    borderRight: '1px solid #eee',
  },
  productSize: {
    fontSize: 10,
    color: '#333',
    fontFamily: 'Roboto Condensed, sans-serif',
  },
  memoCell: {
    padding: '4px',
    verticalAlign: 'top',
  },
  productMemo: {
    fontSize: 12,
    color: '#666',
    lineHeight: 1.4,
    fontFamily: 'Roboto Condensed, sans-serif',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  loadingCell: {
    padding: 30,
    textAlign: 'center',
    color: '#999',
    fontSize: 12,
  },
  // 하단 섹션
  bottomSection: {
    display: 'flex',
    gap: 12,
    padding: '8px 12px',
    borderTop: '1px solid #eee',
    backgroundColor: '#fafafa',
  },
  bottomLeft: {
    width: 240,
    flexShrink: 0,
  },
  bottomRight: {
    flex: 1,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
    fontFamily: 'Roboto Condensed, sans-serif',
  },
  imageUploadArea: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  uploadedImageContainer: {
    position: 'relative',
    width: 50,
    height: 50,
  },
  uploadedImage: {
    width: 50,
    height: 50,
    objectFit: 'cover',
    borderRadius: 2,
    border: '1px solid #ddd',
  },
  removeImageBtn: {
    position: 'absolute',
    top: -5,
    right: -5,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: '#e85454',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
    lineHeight: '14px',
    textAlign: 'center',
  },
  uploadBtn: {
    width: 50,
    height: 50,
    border: '1px dashed #ccc',
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: 20,
    color: '#ccc',
    backgroundColor: '#fff',
  },
  textarea: {
    width: '100%',
    height: 50,
    padding: 6,
    border: '1px solid #ddd',
    borderRadius: 2,
    resize: 'none',
    fontSize: 12,
    fontFamily: 'Roboto Condensed, sans-serif',
    boxSizing: 'border-box',
  },
  // 푸터
  footer: {
    padding: '8px 12px',
    borderTop: '1px solid #ddd',
    backgroundColor: '#f8f8f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  totalBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  totalLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    fontFamily: 'Roboto Condensed, sans-serif',
  },
  totalPrice: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#c77b46',
    fontFamily: 'Roboto Condensed, sans-serif',
  },
  buttonBox: {
    display: 'flex',
    gap: 6,
  },
  cancelBtn: {
    padding: '0 16px',
    height: 32,
    border: '1px solid #333',
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    cursor: 'pointer',
    fontFamily: 'Roboto Condensed, sans-serif',
    backgroundColor: '#fff',
  },
  confirmBtn: {
    padding: '0 16px',
    height: 32,
    backgroundColor: '#251f1b',
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
    cursor: 'pointer',
    fontFamily: 'Roboto Condensed, sans-serif',
  },
};

export default withTranslation('translations')(AddOnOptionModal);
